import { memo, useEffect, useState } from 'react';
import { Handle, Position } from 'reactflow';
import { Eye, X, Download, CheckCircle, AlertCircle, Image, Video, Music } from 'lucide-react';
import { useWorkflowStore } from '../../store/workflowStore';
import { forceDownload } from '../../utils/mediaUtils';
import SendToTimelineButton from '../SendToTimelineButton';

const OutputPreviewNode = memo(({ id, data, selected }) => {
  const deleteNode = useWorkflowStore((state) => state.deleteNode);
  const updateNodeData = useWorkflowStore((state) => state.updateNodeData);
  const edges = useWorkflowStore((state) => state.edges);
  const [mediaError, setMediaError] = useState(false);

  // Check if this node has an input connected
  const hasInput = edges.some((edge) => edge.target === id);
  // Envío LMX-6c: suscripción en vivo al nodo de origen. Antes se leía con
  // getInputForNode (sin suscripción) y la vista previa sólo se actualizaba al
  // tocarla o recargar cuando el resultado llegaba desde LMX / MCP.
  const inputData = useWorkflowStore((state) => {
    const edge = state.edges.find((e) => e.target === id);
    if (!edge) return null;
    return state.nodes.find((n) => n.id === edge.source)?.data || null;
  });
  const outputUrl = inputData?.outputUrl;
  const assetId = inputData?.assetId || null;
  const outputType = inputData?.outputType || 'image'; // 'image', 'video', or 'audio'

  // Reset broken-media flag when the output URL changes
  useEffect(() => { setMediaError(false); }, [outputUrl]);

  // Pass-through: Update this node's data when input changes so it can be used as output
  useEffect(() => {
    if (inputData?.hasOutput && outputUrl) {
      updateNodeData(id, {
        hasOutput: true,
        outputUrl: outputUrl,
        assetId: assetId,
        outputType: outputType,
        file: inputData.file,
        fileName: inputData.fileName,
      });
    } else if (!hasInput || !inputData?.hasOutput) {
      updateNodeData(id, {
        hasOutput: false,
        outputUrl: null,
        assetId: null,
        outputType: null,
      });
    }
  }, [id, inputData, outputUrl, assetId, outputType, hasInput, updateNodeData]);

  const handleDownload = () => {
    if (!outputUrl) return;
    const ext = outputType === 'video' ? 'mp4' : outputType === 'audio' ? 'mp3' : 'png';
    forceDownload(outputUrl, `output_${Date.now()}.${ext}`);
  };

  // Determine icon based on output type
  const getTypeIcon = () => {
    switch (outputType) {
      case 'video': return <Video size={12} />;
      case 'audio': return <Music size={12} />;
      default: return <Image size={12} />;
    }
  };

  const getTypeLabel = () => {
    switch (outputType) {
      case 'video': return 'Video';
      case 'audio': return 'Audio';
      default: return 'Imagen';
    }
  };

  return (
    <div className={`node-container output-node ${selected ? 'selected' : ''}`} data-testid="output-preview-node">
      {/* Input Handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="input"
        className="node-handle input-handle"
      />
      
      {/* Header */}
      <div className="node-header output-header">
        <div className="node-header-left">
          <Eye size={16} />
          <span>Vista Previa</span>
        </div>
        <button className="node-delete-btn" onClick={() => deleteNode(id)}>
          <X size={14} />
        </button>
      </div>
      
      {/* Content */}
      <div className="node-content">
        {outputUrl ? (
          <div className="node-output-preview">
            {/* Type badge */}
            <div className="output-type-badge">
              {getTypeIcon()}
              <span>{getTypeLabel()}</span>
            </div>
            
            {/* Media preview based on type */}
            {mediaError ? (
              <div className="node-output-broken" data-testid="output-media-error">
                <AlertCircle size={20} />
                <span>No se pudo cargar el archivo</span>
              </div>
            ) : outputType === 'video' ? (
              <video
                src={outputUrl}
                controls
                className="node-output-video"
                onError={() => setMediaError(true)}
              />
            ) : outputType === 'audio' ? (
              <div className="node-output-audio">
                <Music size={32} className="audio-icon" />
                <audio
                  src={outputUrl}
                  controls
                  className="audio-player"
                  onError={() => setMediaError(true)}
                />
              </div>
            ) : (
              <img
                src={outputUrl}
                alt="Output"
                className="node-output-image"
                onError={() => setMediaError(true)}
              />
            )}
            
            <div className="node-output-actions">
              <button className="node-download-btn" onClick={handleDownload}>
                <Download size={14} />
                Descargar
              </button>
              {(outputType === 'image' || outputType === 'video') && (
                <SendToTimelineButton
                  assetId={assetId}
                  nodeId={id}
                  url={outputUrl}
                  kind={outputType}
                  label={inputData?.fileName || (outputType === 'image' ? 'Imagen' : 'Video')}
                  testIdPrefix="output-preview-timeline"
                />
              )}
            </div>
            <div className="node-output-badge">
              <CheckCircle size={12} />
              <span>Listo para descargar</span>
            </div>
            
            {/* Pass-through indicator */}
            <div className="output-passthrough-hint">
              <span>→ Conecta la salida para continuar el flujo</span>
            </div>
          </div>
        ) : hasInput ? (
          <div className="node-waiting-compact">
            <Image size={20} className="waiting-icon" />
            <p>Esperando resultado...</p>
          </div>
        ) : (
          <div className="node-empty-compact">
            <AlertCircle size={20} className="empty-icon" />
            <p>Sin entrada</p>
          </div>
        )}
      </div>

      {/* Output Handle - Pass-through: outputs whatever came in */}
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        className="node-handle output-handle"
        isConnectable={!!data.hasOutput}
      />
    </div>
  );
});

OutputPreviewNode.displayName = 'OutputPreviewNode';

export default OutputPreviewNode;
